import React, { useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

const DEFAULT_TITLE =
  "Square Trigger | Digital Agency Vancouver - Website Development & SEO";
const DEFAULT_DESCRIPTION =
  "Square Trigger is a Vancouver-based digital agency specializing in website development and SEO to drive measurable growth across Vancouver and Canada.";

const SEO = ({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  keywords,
  ogTitle,
  ogDescription,
  ogType = "website",
  noIndex = false,
}) => {
  const location = useLocation();

  const CANONICAL_ORIGIN = "https://squaretrigger.com";

  const canonicalUrl = useMemo(() => {
    let normalizedPath = location.pathname === "/" ? "" : location.pathname;

    if (normalizedPath === "/blog") {
      normalizedPath = "/blogs";
    } else if (normalizedPath.startsWith("/blog/")) {
      normalizedPath = normalizedPath.replace("/blog/", "/blogs/");
    } else if (normalizedPath === "/company/about") {
      normalizedPath = "/about";
    } else if (normalizedPath === "/resources/faqs") {
      normalizedPath = "/resources-faqs";
    }

    return `${CANONICAL_ORIGIN}${normalizedPath}`.replace(/\/+$/, "");
  }, [location.pathname]);

  const safeTitle = (title ?? DEFAULT_TITLE).trim() || DEFAULT_TITLE;
  const safeDescription =
    (description ?? DEFAULT_DESCRIPTION).trim() || DEFAULT_DESCRIPTION;

  const safeKeywords = Array.isArray(keywords)
    ? keywords.join(", ")
    : keywords?.trim?.() || "";

  const websiteSchema = {
    "@context": "https://schema.org/",
    "@type": "WebSite",
    name: "Square Trigger",
    url: `${CANONICAL_ORIGIN}/`,
    potentialAction: {
      "@type": "SearchAction",
      target: `${CANONICAL_ORIGIN}/blogs?s={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Square Trigger",
    url: `${CANONICAL_ORIGIN}/`,
    logo: `${CANONICAL_ORIGIN}/favicon.ico`,
    sameAs: [],
  };

  return (
    <Helmet>
      <title>{safeTitle}</title>

      <meta name="description" content={safeDescription} />
      {safeKeywords ? <meta name="keywords" content={safeKeywords} /> : null}

      <meta
        name="robots"
        content={noIndex ? "noindex, nofollow" : "index, follow"}
      />

      <link rel="canonical" href={canonicalUrl} />

      <meta property="og:title" content={(ogTitle || safeTitle).trim()} />
      <meta
        property="og:description"
        content={(ogDescription || safeDescription).trim()}
      />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={ogType} />

      {location.pathname === "/" ? (
        <script type="application/ld+json">
          {JSON.stringify(websiteSchema)}
        </script>
      ) : null}

      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
    </Helmet>
  );
};

export default SEO;